'use client';

import type { MouseEvent } from 'react';
import flags from 'react-phone-number-input/flags';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { parsePhoneDisplay } from '@/utils/formatPhoneNumber';

// nomes curtos ("Brasil"), não os oficiais longos do locale da lib
const regionNamesPt = new Intl.DisplayNames(['pt'], { type: 'region' });

interface PhoneNumberProps {
    phone?: string | null;
    className?: string;
    /**
     * false → display puro (sem copy, click propaga). Pra rows de lista cujo
     * click abre o contato — copiar fica pra dentro do contato aberto.
     */
    interactive?: boolean;
}

/**
 * Exibição canônica de telefone: bandeira do país + número no formato
 * internacional, click copia o número formatado (zona de copy — não
 * dispara o onClick do row/card que embute o número).
 */
export function PhoneNumber({ phone, className, interactive = true }: PhoneNumberProps) {
    if (!phone) return null;

    const { formatted, country } = parsePhoneDisplay(phone);
    const Flag = country ? flags[country] : undefined;
    const countryName = country ? (regionNamesPt.of(country) ?? country) : undefined;

    const flag = Flag && (
        <span className="flex h-3.5 w-5 shrink-0 overflow-hidden rounded-[2px] [&_svg]:size-full">
            <Flag title={countryName ?? ''} />
        </span>
    );

    if (!interactive) {
        return (
            <span className={cn('inline-flex max-w-full items-center gap-1.5', className)}>
                {flag}
                <span className="truncate">{formatted}</span>
            </span>
        );
    }

    async function handleCopy(event: MouseEvent<HTMLButtonElement>) {
        event.stopPropagation();
        event.preventDefault();
        try {
            await navigator.clipboard.writeText(formatted);
            toast.success('Número copiado.');
        } catch {
            toast.error('Não foi possível copiar — selecione e copie manualmente.');
        }
    }

    return (
        <button
            type="button"
            onClick={handleCopy}
            title="Copiar número"
            /* `outline-none`: o `*` do globals.css aplica `outline-ring/50`, e num
               botão que é só o número o anel vira uma caixa em volta do telefone
               — dentro de um Popover ela aparece sozinha, porque o Radix leva o
               foco pro primeiro focável ao abrir. O foco não fica INVISÍVEL: o
               sublinhado do hover passa a valer também pro `focus-visible`, que
               é a mesma pista, no mesmo lugar, sem desenhar caixa. */
            className={cn(
                'inline-flex max-w-full cursor-pointer items-center gap-1.5 text-left outline-none hover:underline focus-visible:underline',
                className
            )}
        >
            {flag}
            <span className="truncate">{formatted}</span>
        </button>
    );
}
